import { numberValue, record, records, textValue, type ReportPageCode, type ReportingPayload } from './reporting-model'
import { displayDomainValue } from '../lib/display-labels'

export const BOARD_CHART_CODES = [
  'initiative_maturity',
  'business_area',
  'governance_position',
  'strategic_alignment',
  'roadmap_activity',
  'risk_matrix',
  'risk_categories',
  'governance_workload',
  'material_changes',
] as const

export type BoardChartCode = typeof BOARD_CHART_CODES[number]

export const BOARD_CHART_CATALOGUE: ReadonlyArray<{
  code: BoardChartCode
  title: string
  subtitle: string
  page: ReportPageCode
}> = [
  { code: 'initiative_maturity', title: 'AI initiative maturity', subtitle: 'Planned initiatives and initiatives with active AI uses over time.', page: 'executive_overview' },
  { code: 'business_area', title: 'AI uses by business area', subtitle: 'Current recorded AI uses, ordered by business area.', page: 'ai_portfolio_governance' },
  { code: 'governance_position', title: 'Governance position', subtitle: 'Current AI uses by recorded decision position.', page: 'ai_portfolio_governance' },
  { code: 'strategic_alignment', title: 'AI alignment by strategic priority', subtitle: 'AI Initiatives and AI Uses linked to each strategic priority.', page: 'strategy_roadmap' },
  { code: 'roadmap_activity', title: 'Initiative activity across the roadmap', subtitle: 'Initiatives starting and continuing in each quarter.', page: 'strategy_roadmap' },
  { code: 'risk_matrix', title: 'Residual risk position', subtitle: 'Current AI risks by residual likelihood and consequence.', page: 'risk_position' },
  { code: 'risk_categories', title: 'Current risks by category', subtitle: 'Current risk counts and recorded tolerance position.', page: 'risk_position' },
  { code: 'governance_workload', title: 'Open governance work', subtitle: 'Current work requiring governance coordination.', page: 'governance_actions_change' },
  { code: 'material_changes', title: 'Material changes since the previous report', subtitle: 'Recorded material changes by type.', page: 'governance_actions_change' },
]

export type BoardChartRow = {
  label: string
  values: number[]
}

export type RoadmapChartPoint = {
  label: string
  quarterStart: string
  planned: number
  active: number | null
  total: number
  quality: string
}

export type RoadmapActivityPoint = {
  label: string
  quarterStart: string
  starting: number
  continuing: number
  total: number
}

export function boardChartDefinition(code: BoardChartCode) {
  return BOARD_CHART_CATALOGUE.find((chart) => chart.code === code) ?? BOARD_CHART_CATALOGUE[0]!
}

export function boardChartsForPage(code: ReportPageCode): BoardChartCode[] {
  if (code === 'executive_overview') return ['initiative_maturity']
  if (code === 'ai_portfolio_governance') return ['business_area', 'governance_position']
  if (code === 'strategy_roadmap') return ['strategic_alignment', 'roadmap_activity']
  if (code === 'risk_position') return ['risk_matrix', 'risk_categories']
  return ['governance_workload', 'material_changes']
}

export function wrapBoardLabel(value: unknown, maxChars = 52, maxLines = 3): string[] {
  const words = textValue(value, 'Not recorded').trim().split(/\s+/).filter(Boolean)
  const lines: string[] = []
  for (const word of words) {
    const current = lines.at(-1)
    if (!current || current.length + word.length + 1 > maxChars) lines.push(word)
    else lines[lines.length - 1] = `${current} ${word}`
  }
  if (lines.length <= maxLines) return lines
  const visible = lines.slice(0, maxLines - 1)
  visible.push(lines.slice(maxLines - 1).join(' '))
  return visible
}

export function topBoardRows(rows: BoardChartRow[], maximum = 8, otherLabel = 'Other'): BoardChartRow[] {
  const clean = rows
    .map((row) => ({ label: row.label || 'Not recorded', values: row.values.map((value) => Math.max(0, numberValue(value))) }))
    .sort((a, b) => b.values.reduce((sum, value) => sum + value, 0) - a.values.reduce((sum, value) => sum + value, 0) || a.label.localeCompare(b.label))
  if (clean.length <= maximum) return clean
  const visible = clean.slice(0, maximum - 1)
  const hidden = clean.slice(maximum - 1)
  const seriesCount = Math.max(1, ...clean.map((row) => row.values.length))
  return [...visible, {
    label: otherLabel,
    values: Array.from({ length: seriesCount }, (_, index) => hidden.reduce((sum, row) => sum + (row.values[index] ?? 0), 0)),
  }]
}

function centredQuarterWindow<T extends { quarterStart: string }>(points: T[], asOf: unknown, maximum = 10): T[] {
  if (points.length <= maximum) return points
  const asOfDate = new Date(textValue(asOf))
  const currentIndex = Number.isNaN(asOfDate.getTime()) ? -1 : points.findIndex((point, index) => {
    const start = new Date(point.quarterStart)
    const next = index + 1 < points.length ? new Date(points[index + 1]!.quarterStart) : new Date(start.getFullYear(), start.getMonth() + 3, 1)
    return asOfDate >= start && asOfDate < next
  })
  let start = currentIndex < 0 ? Math.max(0, points.length - maximum) : Math.max(0, currentIndex - 4)
  start = Math.min(start, points.length - maximum)
  return points.slice(start, start + maximum)
}

export function roadmapChartPoints(payload: ReportingPayload): RoadmapChartPoint[] {
  const growth = record(record(payload.strategy).roadmapGrowth)
  const points = records(growth.points).map((point) => ({
    label: textValue(point.label),
    quarterStart: textValue(point.quarterStart),
    planned: numberValue(point.plannedInitiatives),
    active: point.activeInitiatives === null || point.activeInitiatives === undefined ? null : numberValue(point.activeInitiatives),
    total: numberValue(point.totalInitiatives),
    quality: textValue(point.quality, 'unavailable'),
  }))
  return centredQuarterWindow(points, payload.asOf)
}

export function roadmapActivityPoints(payload: ReportingPayload): RoadmapActivityPoint[] {
  const activity = record(record(payload.strategy).roadmapActivity)
  const points = records(activity.points).map((point) => ({
    label: textValue(point.label),
    quarterStart: textValue(point.quarterStart),
    starting: numberValue(point.startingInitiatives),
    continuing: numberValue(point.continuingInitiatives),
    total: numberValue(point.totalInitiatives),
  }))
  return centredQuarterWindow(points, payload.asOf)
}

export function businessAreaRows(payload: ReportingPayload): BoardChartRow[] {
  return topBoardRows(records(record(payload.portfolio).businessAreas).map((area) => ({
    label: textValue(area.label, 'Business area not recorded'),
    values: [numberValue(area.total)],
  })), 8, 'Other business areas')
}

export function governancePositionRows(payload: ReportingPayload): BoardChartRow[] {
  return topBoardRows(records(record(payload.portfolio).approvalPositions).map((position) => ({
    label: textValue(position.label, 'Not yet assessed'),
    values: [numberValue(position.count)],
  })), 8, 'Other recorded positions')
}

export function governancePositionChartRows(payload: ReportingPayload): BoardChartRow[] {
  return governancePositionRows(payload).filter((row) => numberValue(row.values[0]) > 0)
}

export function governancePositionChartMode(rows: BoardChartRow[]): 'empty' | 'donut' | 'horizontal-bars' {
  if (!rows.length) return 'empty'
  return rows.length >= 2 && rows.length <= 4 ? 'donut' : 'horizontal-bars'
}

export function donutSegmentPath(cx: number, cy: number, outerRadius: number, innerRadius: number, startAngle: number, endAngle: number): string {
  const point = (radius: number, angle: number) => {
    const radians = angle * Math.PI / 180
    return { x: cx + radius * Math.cos(radians), y: cy + radius * Math.sin(radians) }
  }
  const outerStart = point(outerRadius, startAngle)
  const outerEnd = point(outerRadius, endAngle)
  const innerEnd = point(innerRadius, endAngle)
  const innerStart = point(innerRadius, startAngle)
  const largeArc = endAngle - startAngle > 180 ? 1 : 0
  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${innerStart.x} ${innerStart.y}`,
    'Z',
  ].join(' ')
}

export function strategicAlignmentRows(payload: ReportingPayload): BoardChartRow[] {
  const priorities = records(record(record(payload.strategy).organisationStrategy).priorities)
  return topBoardRows(priorities.map((priority) => ({
    label: textValue(priority.title, 'Priority not recorded'),
    values: [numberValue(priority.initiativeCount), numberValue(priority.useCaseCount)],
  })), 8, 'Other priorities')
}

export function riskCategoryRows(payload: ReportingPayload): BoardChartRow[] {
  return topBoardRows(records(record(payload.risk).categories).map((category) => ({
    label: textValue(category.label, 'Category not recorded'),
    values: [numberValue(category.withinTolerance), numberValue(category.approachingTolerance), numberValue(category.outsideTolerance), numberValue(category.notRated)],
  })), 8, 'Other risk categories')
}

export function governanceWorkloadRows(payload: ReportingPayload): BoardChartRow[] {
  return topBoardRows(records(record(payload.governance).workload).map((item) => ({
    label: textValue(item.label, 'Governance work'),
    values: [numberValue(item.count)],
  })), 8, 'Other governance work')
}

export function materialChangeRows(payload: ReportingPayload): BoardChartRow[] {
  return topBoardRows(records(record(payload.governance).materialChangeTypes).map((item) => ({
    label: displayDomainValue(textValue(item.label) || textValue(item.code), 'Other'),
    values: [numberValue(item.count)],
  })), 8, 'Other change types')
}

export function niceMaximum(values: number[]): number {
  const maximum = Math.max(0, ...values)
  if (maximum <= 0) return 1
  const magnitude = 10 ** Math.floor(Math.log10(maximum))
  const normalised = maximum / magnitude
  const rounded = normalised <= 1 ? 1 : normalised <= 2 ? 2 : normalised <= 5 ? 5 : 10
  return rounded * magnitude
}
